/** @jsxImportSource @emotion/react */

import { Modal } from "components/Modal";
import {
  NOTIFICATION_TYPE,
  NotificationContext,
} from "context/NotificationContext";
import { UserContext } from "context/UserContext";
import { useContext, useEffect, useState } from "react";
import { ReactTabulator } from "react-tabulator";
import RecordService from "services/RecordService";
import tw from "twin.macro";
import { formatDate } from "utils/helpers";

export const DetailTable = ({
  data,
  setShowNotesModal,
  isQuestion,
  setIsQuestion,
  discrepData,
  discrepType,
}) => {
  const recordService = new RecordService("discrep");
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const [newStatus, setNewStatus] = useState<string>(data?.status);

  const [discrepDetails, setDiscrepDetails] = useState({
    Status: "",
    Question: 0,
    Type: "",
    "Created By": "",
    "Last Modified": "",
    Opened: "",
  });

  const stateOptions = ["Unvetted", "New", "Completed"];
  const carrierOptions = ["Notified", "Resolved"];

  useEffect(() => {
    setDiscrepDetails({
      Status: data?.status,
      Question: data?.question,
      Type: data?.dtype,
      "Created By": data.submitted_by,
      "Last Modified": data?.last_modified,
      Opened: data.submitted,
    });
    setNewStatus(data.status);
    setIsQuestion(data?.question);
  }, [data, setIsQuestion]);

  const updateStatus = async (status) => {
    try {
      const res = await recordService.updateDiscrepancy(
        discrepType,
        {
          id: data.id,
          // table_name: data.table_name,
          Criteria: [
            {
              field: "status",
              value: status,
            },
          ],
        },
        undefined,
        data?.question,
      );

      if (res.response_code === 200) {
        addNotification({
          title: "Success",
          message: "Status updated",
          type: NOTIFICATION_TYPE.SUCCESS,
        });
        data.status = newStatus;
      } else {
        console.log(res);
        addNotification({
          title: "Error",
          message: "Unable to update status",
          type: NOTIFICATION_TYPE.ERROR,
        });
        setNewStatus(data.status);
      }
    } catch (error) {
      addNotification({
        title: "Error",
        message: "Unable to update status",
        type: NOTIFICATION_TYPE.ERROR,
      });
      setNewStatus(data.status);
    }
  };

  return (
    <table css={[tw`w-full`]}>
      <tbody>
        {Object.keys(discrepDetails).map((field) => (
          <tr key={field} css={[tw`border-b border-gray-300`]}>
            <td css={[tw`font-semibold border-r border-gray-400 p-2`]}>
              {field}
            </td>
            <td css={[tw`p-2`]}>
              {field === "Status" ? (
                <div css={[tw`flex w-full justify-between content-start`]}>
                  <select
                    css={[tw`py-1`]}
                    name="status"
                    id="status-select"
                    value={newStatus}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewStatus(value);
                      updateStatus(value);
                    }}
                  >
                    {(newStatus ?? discrepDetails.Status ?? "") === "" ? (
                      <option id="" value=""></option>
                    ) : (
                      <option
                        id={newStatus ?? discrepDetails.Status ?? ""}
                        key={newStatus ?? discrepDetails.Status ?? ""}
                        value={newStatus ?? discrepDetails.Status ?? ""}
                        disabled
                      >
                        {newStatus ?? discrepDetails.Status ?? ""}
                      </option>
                    )}
                    {user?.functions?.includes(
                      "foresite:discrep:updateByCarrier",
                    ) &&
                      carrierOptions
                        .filter(
                          (option) =>
                            option !==
                            (newStatus ?? discrepDetails.Status ?? ""),
                        )
                        .map((option) => (
                          <option id={option} key={option} value={option}>
                            {option}
                          </option>
                        ))}
                    {user?.functions?.includes(
                      "foresite:discrep:updateByJurisdiction",
                    ) &&
                      stateOptions
                        .filter(
                          (option) =>
                            option !==
                            (newStatus ?? discrepDetails.Status ?? ""),
                        )
                        .map((option) => (
                          <option id={option} key={option} value={option}>
                            {option}
                          </option>
                        ))}
                  </select>
                </div>
              ) : field === "Question" ? (
                <div css={[tw`flex w-full justify-between content-start`]}>
                  <select
                    value={isQuestion ?? 0}
                    onChange={async (e) => {
                      const value = e.target.value;
                      let current = data?.question;
                      setIsQuestion(Number(value));

                      if (value === "1") {
                        if (value !== current) {
                          setShowNotesModal(true);
                        }
                      } else {
                        try {
                          const question =
                            await recordService.updateDiscrepancy(
                              discrepType,
                              {
                                id: data.id,
                                // table_name: data.table_name,
                                Criteria: [
                                  {
                                    field: "status",
                                    value: data?.status, // keep current status
                                  },
                                ],
                                //   {
                                //     field: "question",
                                //     value: "0",
                                //   },
                                // ],
                              },
                              undefined,
                              0,
                            );
                          console.log(question);
                          addNotification({
                            title: "Question Updated",
                            message: "Question has been updated successfully",
                            type: NOTIFICATION_TYPE.SUCCESS,
                          });
                        } catch (error) {
                          addNotification({
                            title: "Error",
                            message: `Unable to update question`,
                            type: NOTIFICATION_TYPE.ERROR,
                          });
                          setIsQuestion(current);
                        }
                      }
                    }}
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>
              ) : (
                discrepDetails[field]
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const NotesTable = ({ data, setData }) => {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
  const [note, setNote] = useState({
    note: "",
    submitted_by: "",
    submitted: "",
  });
  const clickNote = (e, row) => {
    const data = row.getData();
    setNote(data);
    setIsOpen(true);
  };

  const css = `tabulator .tabulator-table .tabulator-row .tabulator-cell {
    white-space: normal;
    text-overflow: clip; 
  }`;

  return (
    <>
      <ReactTabulator
        id="notes-table"
        className="tabulator tabulator-top-0"
        style={{ css }}
        data={data}
        events={{ rowClick: clickNote }}
        options={{
          placeholder: `<div style="width: 100%; text-align: center; margin: auto; display: flex; justify-content: center; align-items: center; min-height: 130px;">No Notes</div>`,
          minHeight: "240",
          height: "240",
          layout: "fitColumns",
          pagination: true,
          paginationSize: 5,
          initialSort: [
            //set the initial sort order of the data
            { column: "submitted", dir: "desc" },
          ],
        }}
        columns={[
          { title: "Note", field: "note", formatter: "textarea", width: 290 },
          { title: "Author", field: "submitted_by" },
          { title: "Created", field: "submitted" },
        ]}
      />
      <Modal
        width="s"
        scale="s"
        title={`${formatDate(note.submitted)}`}
        content={
          <div>
            <h4>{note.submitted_by}</h4> <p>{note?.note}</p>
          </div>
        }
        isOpen={isOpen}
        onClose={() => setIsOpen(undefined)}
      />
    </>
  );
};
